import { z } from "zod";

/// URL-backed filter state for the /insights shell and every tab panel
/// mounted into it. `tab` selects the panel; `window`, `depts`, and
/// `metric` are the shared filters the toolbar writes.
///
/// `insightAudience`/`insightScope`/`insightStatus`/`insightSeverity`/
/// `insightId` (Task 11) are the Queue tab's triage filters + detail
/// sheet deep link, copied verbatim (names, enums, `.optional()` fallback
/// behavior) from `workspace.route.search.ts` so `InsightQueuePanel` and
/// `InsightDetailSheet` read the same shape from either route.
export const insightsSearchSchema = z.object({
  /// Visible tabs are "overview" | "analyze" | "queue". The other values
  /// are legacy: "explore" plus its three sub-lenses ("drivers"/
  /// "departments"/"themes") all became the single Analyze workbench, and
  /// "surveys"/"actions"/"brief" fold into Overview. They stay in the enum
  /// because ~15 deep links across Home, pulse-dashboards, pulse-actions,
  /// and the legacy /dashboard redirects still emit them, and because old
  /// bookmarks must round-trip; `resolveInsightsTab` maps them onto the new
  /// shape instead of `.catch` silently dropping them.
  tab: z
    .enum([
      "overview",
      "analyze",
      "queue",
      "explore",
      "drivers",
      "departments",
      "surveys",
      "themes",
      "actions",
      "brief",
    ])
    .catch("overview"),
  window: z.enum(["7d", "30d", "90d"]).catch("30d"),
  // `.catch([])` alone resolves a missing/invalid `depts` param to `[]`. Do
  // not chain a trailing `.default("")` (as comparison.route.search.ts
  // warns against): ZodDefault short-circuits on `undefined` input before
  // the inner transform runs, so a missing `depts` key would resolve to the
  // raw string `""` instead of `[]`, breaking every array-typed consumer.
  //
  // Must also accept an already-split `string[]`, not just the raw csv
  // `string`: TanStack Router's `matchRoutesLightweight` (used by
  // `buildLocation`/`buildAndCommitLocation` on every `navigate()`, including
  // the router's own URL canonicalization on initial load) re-runs every
  // matched route's `validateSearch` against its OWN previously accumulated
  // search object, not just the raw URL string. Once this schema has parsed
  // `depts` once (string -> string[]), a second pass in the same
  // `matchRoutesLightweight` sweep hands the schema the array it just
  // produced. A `z.string()`-only schema rejects that array (wrong type)
  // and `.catch([])` silently wipes the selection, which is exactly what
  // made the department FilterBar's checkboxes look like they did nothing
  // (live-verified: a plain `?depts=eng` URL was rewritten to `depts=[]` by
  // the router before React ever rendered). Accepting the array form too
  // makes the parse idempotent, so re-validating already-parsed output is a
  // no-op instead of data loss.
  depts: z
    .union([z.array(z.string()), z.string()])
    .transform((v) => (Array.isArray(v) ? v : v.split(",").filter(Boolean)))
    .catch([]),
  metric: z.enum(["dimension", "wedge"]).catch("dimension"),
  insightAudience: z.enum(["manager", "people_ops", "owner"]).optional(),
  insightScope: z.enum(["workspace", "department", "membership"]).optional(),
  insightStatus: z.enum(["generated", "shown", "reviewed", "dismissed", "expired"]).optional(),
  insightSeverity: z.enum(["low", "medium", "high", "critical"]).optional(),
  /// Insight opened in the detail sheet. In the URL so a triaged insight is
  /// shareable and the action-detail "source insight" link can deep-link it.
  insightId: z.string().optional(),
  /// Department opened in the Analyze tab's heatmap drill-down drawer.
  /// Optional passthrough, so `?tab=departments&dept=<id>` (the legacy form
  /// every `dept-detail-link` still emits) round-trips and is shareable.
  dept: z.string().optional(),
  /// Dimension or wedge metric id opened in the Analyze tab's driver
  /// drill-down drawer. Same optional passthrough shape as `dept`.
  driver: z.string().optional(),
  /// Theme opened in the Analyze tab's theme drill-down drawer. Same
  /// optional passthrough shape as `dept`/`driver`; the theme name itself
  /// is the identifier (the shore's theme endpoints key on it directly).
  ///
  /// All three drawers now coexist on one tab, so a legacy link carrying
  /// both a tab value and its id (e.g. `?tab=themes&theme=Workload`) lands
  /// on Analyze with exactly the right drawer open, no lens switch needed.
  theme: z.string().optional(),
  /// Workspace-brief audience switcher (Owner / People Ops lens) on the
  /// Overview tab. Copied verbatim (name, enum, `.catch` fallback) from the
  /// retired `workspace.route.search.ts`, so an old
  /// `/workspace?briefAudience=owner` bookmark carries the same value.
  briefAudience: z.enum(["people_ops", "owner"]).catch("people_ops"),
});

export type InsightsSearch = z.infer<typeof insightsSearchSchema>;

/// The pre-parse shape: `depts` is the raw csv string here (it's an array
/// only after `insightsSearchSchema` transforms it). TanStack Router's
/// `navigate({ search })` updater must return this shape, not the parsed
/// `InsightsSearch`, since the value it returns is what gets serialized
/// into the URL and re-parsed on the next render.
export type InsightsSearchInput = z.input<typeof insightsSearchSchema>;
